import { DataFieldType } from '~/enums/DataFieldType';
import { DateFormat } from '~/i18n/hooks/useFormatDate/utils';
import { PilotId } from '~/types/PilotId';
import { getUniqueFieldValues } from '~/utils/document.utils';

import { EditData, FieldValue, OverviewSection } from './types';

type FormatDateFn = (
  date: Date | string | number | null | undefined,
  format?: DateFormat,
) => string;

export const processSectionItems = (
  sections: OverviewSection[],
  formatDate: FormatDateFn,
): EditData => {
  let returnObject: EditData = {};

  if (!sections || !sections.length) return returnObject;

  sections.forEach((section) => {
    const formattedFields = section.fields.reduce<EditData>((total, field) => {
      const newObject: EditData = {};

      if (
        [DataFieldType.ARRAY_MULTIPLE, DataFieldType.ATTACHMENT].includes(
          field.type,
        )
      ) {
        const values = getUniqueFieldValues(field.value as FieldValue[]);
        newObject[field.id] = values.map((val) => ({
          label: val.display_value,
          value: val.value,
        }));
      } else if (field.type === DataFieldType.ARRAY_SINGLE) {
        const singleValue = field.value as FieldValue | null;
        newObject[field.id] = singleValue
          ? { label: singleValue.display_value, value: singleValue.value }
          : null;
      } else if (field.type === DataFieldType.DATE) {
        const dateValue = field.value as FieldValue | null;
        newObject[field.id] = dateValue
          ? formatDate(dateValue.value.toString(), 'iso')
          : null;
      } else if (field.type === DataFieldType.REFERENCE) {
        const refValue = field.value as FieldValue | null;
        newObject[field.id] = refValue ? refValue.display_value : null;
      } else {
        const simpleValue = field.value as FieldValue | null;
        newObject[field.id] = simpleValue ? simpleValue.value : null;
      }

      return { ...total, ...newObject };
    }, {});

    returnObject = { ...returnObject, ...formattedFields };
  });

  return returnObject;
};

interface PreprocessedItem {
  id: PilotId;
  value: string | number | (string | number)[] | null;
}

export const preprocessEditData = (data: EditData): PreprocessedItem[] =>
  Object.keys(data).map((dataKey) => {
    const fieldId = parseInt(dataKey) as PilotId;
    const fieldValue = data[fieldId];
    let processedValue: string | number | (string | number)[] | null;

    if (Array.isArray(fieldValue)) {
      processedValue = fieldValue.map((i) => i.value);
    } else if (
      fieldValue &&
      typeof fieldValue === 'object' &&
      'value' in fieldValue
    ) {
      processedValue = fieldValue.value;
    } else {
      processedValue = fieldValue as string | number | null;
    }

    return {
      id: fieldId,
      value: processedValue,
    };
  });

export const capitalizeString = (string: string | number | boolean): string => {
  let returnString = string;

  if (typeof returnString !== 'string') {
    returnString = returnString.toString();
  }

  return returnString.charAt(0).toUpperCase() + returnString.slice(1);
};

export const booleanOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const getSingleFieldValue = (
  value: FieldValue | FieldValue[] | null | undefined,
): FieldValue | null => {
  if (!value || Array.isArray(value)) {
    return null;
  }
  return value;
};

export const extractSelectValue = (
  presentValue: any,
): string | number | null => {
  if (!presentValue) {
    return null;
  }

  if (
    typeof presentValue === 'object' &&
    !Array.isArray(presentValue) &&
    'value' in presentValue
  ) {
    return presentValue.value;
  }

  if (typeof presentValue === 'string' || typeof presentValue === 'number') {
    return presentValue;
  }

  return null;
};

export const coerceInputValueToNumber = (inputValue: any): number | null => {
  if (inputValue === '' || inputValue === null || inputValue === undefined) {
    return null;
  }
  const valueToConvert =
    typeof inputValue === 'object' && 'value' in inputValue
      ? inputValue.value
      : inputValue;
  return Number(valueToConvert);
};
